import { Injectable } from '@angular/core';
import { IContextData } from './contexts/common';
import {ENS} from './contexts/ens';
import {MPT} from './contexts/mpt';
import {VEA} from './contexts/vea';
import {EQAO} from './contexts/eqao';
import {BCED} from './contexts/bced';
import {ANON} from './contexts/anon';
import { environment } from "../../environments/environment";
import { isLocalApiUrlOverrideEnabled } from "../../environments/dev-context";
import * as moment from 'moment-timezone';
import { tzLangAbbrs } from '../core/util/moment';

const availableContexts:IContextData[] = [ // first entry is also the default
  EQAO,
  //BCED,
  VEA,
  ANON
  //MPT,
  // MEES,
  // ENS,
];

export enum FLAGS 
{
  ABED = "ABED",
  NBED = "NBED",
  EQAO = "EQAO",
  BCED = "BCED",
  MBED = "MBED",
  CAEC = "CAEC",
}

export const TEMPLATE_BRAND_NAME = '%BRAND_NAME%';

// Staging API URL override (query / localStorage) — https staging hosts only.
// Local multi-port API pin is build/serve-time: environment.localApiUrl via
// `npm run start:local-api -- --api-port=3032`, gated by context.local.json
// `dev.useLocalApiUrlOverride` (not a URL query param).
const API_URL_STORAGE_KEY = "apiUrlOverrideV2";
const ALLOWED_API_OVERRIDE_HOSTS = ["vea-api-staging.vretta.com", "vea-api-staging-k8s.vretta.com"];

function getStagingApiUrlOverride(): string | null {
  const params = new URLSearchParams(window.location.search);
  const apiParam = params.get("apiUrl");
  if (apiParam !== null) {
    if (apiParam === "") {
      localStorage.removeItem(API_URL_STORAGE_KEY);
      console.info("API address override cleared by empty query param.");
      return null;
    }
    try {
      const url = new URL(apiParam);
      if (url.protocol === "https:" && ALLOWED_API_OVERRIDE_HOSTS.includes(url.hostname)) {
        localStorage.setItem(API_URL_STORAGE_KEY, apiParam);
        console.info("Using API override address from query parameter:", apiParam);
        return apiParam;
      }
    } catch {}
    localStorage.removeItem(API_URL_STORAGE_KEY);
    console.warn("Invalid API override address provided in query parameter:", apiParam);
    return null;
  }
  const stored = localStorage.getItem(API_URL_STORAGE_KEY);
  if (stored) {
    try {
      const url = new URL(stored);
      if (url.protocol === "https:" && ALLOWED_API_OVERRIDE_HOSTS.includes(url.hostname)) {
        console.info("Using API override address from localStorage:", stored);
        return stored;
      }
    } catch {}
    localStorage.removeItem(API_URL_STORAGE_KEY);
    console.warn("Invalid API override address in localStorage, removed:", stored);
  }
  return null;
}

function getBuildTimeLocalApiUrl(): string | null {
  if (environment.production) {
    return null;
  }
  if (!isLocalApiUrlOverrideEnabled()) {
    return null;
  }
  const url = environment.localApiUrl;
  if (!url) {
    return null;
  }
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(parsed.hostname) && parsed.port) {
      console.info('Using build-time local API override:', parsed.origin);
      return parsed.origin;
    }
  } catch {}
  console.warn('Invalid environment.localApiUrl ignored:', url);
  return null;
}

const stagingApiUrlOverride = getStagingApiUrlOverride();
const buildTimeLocalApiUrl = getBuildTimeLocalApiUrl();

@Injectable({
  providedIn: 'root'
})
export class WhitelabelService {

  context:string;
  contextData:IContextData;
  defaultWhitelabelContext:string;

  currentHomepageRoute:string;
  currentLoginRoute:string;
  currentBrandName:string;
  currentLogo:string;

  constructor() {
    const hostMap:{}[] = []
    const contextRef = this.registerContexts(availableContexts);
    console.log(contextRef)
    // const storedWhitelabel = localStorage.getItem('defaultWhitelabel'); // need to check if the data is defined before using
    this.context = contextRef.hostRef.get(window.location.host) || contextRef.defaultContextId;
    console.log('whitelabel context', this.context)
    this.contextData = contextRef.data.get(this.context);
    this.currentHomepageRoute = this.contextData.homepageRoute;
    this.currentLoginRoute = this.contextData.loginRoute;
    this.currentBrandName = this.contextData.brandName;
    this.currentLogo = this.contextData.logo;
    this.ensureDefaultContext();
  }

  updateRouteOnLang(homepageRoute: string, lang: string = 'en') {
    let homepage = homepageRoute;
    if (lang === 'fr') {
      // /en/login-router-st
      const homepageArr = homepageRoute.split('/');
      homepageArr[1] = 'fr';
      homepage = homepageArr.join('/');
    }
    return homepage;
  }

  registerContexts(contexts:IContextData[]){
    type ContextId = string;
    const hostRef:Map<ContextId, string> = new Map();
    const data:Map<ContextId, IContextData> = new Map();
    const defaultContextId = contexts[0].id;
    contexts.forEach(contextData => {
      if (data.has(contextData.id)){
        console.warn('Duplicate WL context definition.', contextData.id);
      }
      else{
        data.set(contextData.id, contextData);
        contextData.hosts.forEach(host => {
          if (hostRef.has(host)){
            console.warn('Duplicate host WL definition.', host, contextData.id, hostRef.get(host));
          }
          else{
            hostRef.set(host, contextData.id);
          }
        });
      }
    });
    return {data, hostRef, defaultContextId};
  }

  ensureDefaultContext(){
    const context = this.contextData;
    if (!context){
      console.error('using undefined context');
    }
    context.siteText = this.contextData.siteText || {};
    context.siteFlags = this.contextData.siteFlags || {};
    context.siteData = this.contextData.siteData || {};
    context.footer = this.contextData.footer || [];
    context.siteText = this.contextData.siteText || {};
    const siteText = this.contextData.siteText;
    if (!siteText['SITE_LINK']){
      siteText['SITE_LINK'] = window.location.host;
    }
  }

  onReady( callback ){
    callback(); // need to delay this if config is loaded over a file
  }

  updateTemplateContent(str:string){
    if (str){
      return str.split(TEMPLATE_BRAND_NAME).join(this.currentBrandName);
    }
    return str;
  }

  getApiAddress() {
    if (buildTimeLocalApiUrl) {
      return buildTimeLocalApiUrl;
    }
    if (!environment.production && stagingApiUrlOverride) {
      return stagingApiUrlOverride;
    }
    if (!environment.production) {
      const hostname = window.location.hostname;
      if (hostname === 'deployments-staging.vretta.com') {
        const milestone = window.location.pathname.split('/')[1];
        if (milestone) return `https://vea-api-staging.vretta.com/${milestone}`;
      }
      if (hostname === 'deployments-staging-k8s.vretta.com') {
        const milestone = window.location.pathname.split('/')[1];
        if (milestone) return `https://vea-api-staging-k8s.vretta.com/${milestone}`;
      }
    }
    const hostname = window.location.hostname;
    const apiHost = hostname === '127.0.0.1' ? 'localhost' : hostname;
    return this.contextData.apiAddress(apiHost);
  }
  getHomepageRoute(){ return this.currentHomepageRoute; }
  getLoginRoute(lang?: string){ 
    return this.currentLoginRoute || this.updateRouteOnLang(this.currentHomepageRoute, lang); 
  }
  getSiteFlag(slug:string){ return this.contextData.siteFlags[slug]; } 
  getSiteData(slug:string){ return this.contextData.siteData[slug]; } 
  getSiteText(slug:string, defaultText:string=''){ return this.contextData.siteText[slug] || defaultText; }
  getFooter(){ return this.contextData.footer; }
  getDefaultCurriculumId(){ return ''; }

  // shortcuts
  isEQAO = () => this.getSiteFlag('IS_EQAO');
  isNBED = () => this.getSiteFlag('IS_NBED'); 
  isABED = () => this.getSiteFlag('IS_ABED'); 
  isMBED = () => this.getSiteFlag('IS_MBED'); 
  isBCED = () => this.getSiteFlag('IS_BCED'); 
  isSchlBoards = () => this.getSiteFlag('IS_SCHL_BOARDS'); 


  // Results Page based on site flag
  isResultPageAvailable = () => this.getSiteFlag('IS_RESULTS_PAGE_AVAILABLE');

  getTimeZone(): string {
    return this.contextData.defaultTimezone;
  }
  
  getTimezoneAbbr(): string {
    return this.contextData.defaultTimezoneAbbr;
  }

  /**
   * Format datetime to Date string according to white label context timezone
   * @param dateTime 
   * @param inputDateTimeTZ 
   * @param lang 
   * @param dateFormat 
   * @returns {string} Formatted datetime string
   */
  formatDateForWhitelabel(dateTime: string, inputDateTimeTZ: string = null, lang: string = 'en', dateFormat?:string): string
  {
    // dateTime is in UTC time if inputDateTimeTZ is null
    if (dateTime == null || dateTime == "")
    {
      return "";
    }

    const inputDateTime = inputDateTimeTZ == null ? moment(dateTime).utc() : moment.tz(dateTime, inputDateTimeTZ);
    const displayTZ = this.getTimeZone();
    let displayTZAbbr = this.getTimezoneAbbr(); 

    if (this.isEQAO()) { // displayTZAbbr did not take into consideration of the switch between EDT/EST timezone
      let mmdateFormat = dateFormat
      if(!dateFormat) {
        mmdateFormat = lang === 'en' ? 'MMMM Do, YYYY h:mm A z' : 'D MMMM YYYY H [h] m z'
      }
      let momentDateTimeStr = inputDateTime.tz(displayTZ).format(mmdateFormat)
      if(lang === 'fr') {
        momentDateTimeStr = tzLangAbbrs(momentDateTimeStr, lang)
      }
      return momentDateTimeStr
    }

    if(this.isABED() && lang === "fr"){
     displayTZAbbr = "HNR"
    }
    if(this.isABED())
    return inputDateTime.tz(displayTZ).format(this.isABED() && lang === "fr" ? "D MMMM YYYY H [h] m" : "MMMM Do, YYYY h:mm A") + " " + displayTZAbbr;
  }

  getRolesSd(){
    return this.contextData.sdRoleTypes || []
  }

  getWhitelabelFlag() 
  {
    if(this.isABED()) {
      return FLAGS.ABED;
    } else if(this.isNBED()) {
      return FLAGS.NBED;
    } else if(this.isEQAO()) {
      return FLAGS.EQAO;
    } else if(this.isBCED()) {
      return FLAGS.BCED;
    } else if(this.isMBED()) {
      return FLAGS.MBED;
    } else {
      return "NA";
    }
  }

}
